import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Target, Plus, X, Edit3, Trash2, Users, User, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/api/client";

/**
 * Department Targets panel — surfaces every team's named monthly deliverables
 * (e.g. "RIIM Videos — 30 for Edit Team, September").
 *
 * Everyone can VIEW.
 * Managers of the target's team + admins + super-admins can CREATE / EDIT / DELETE
 * targets and LOG progress. Team members can LOG progress on their own team.
 *
 * The progress log holds entries {date, count, ...}. The panel derives the
 * per-week breakdown from those dates so we never store denormalised weekly
 * counters — a manager can log 5 on Monday and 3 on Wednesday and the week 1
 * total shows 8 automatically.
 */
const TEAMS = ["Management", "Shoot Team", "Edit Team", "Digital Team", "Sales Team", "Finance Team", "HR + Admin"];

// ISO week start (Mon-anchored) — matches how the Weekly Grid already works.
export default function DepartmentTargetsPanel({ currentUser, teamScope = null, defaultOpen = true, viewKey = "default", month: monthProp }) {
  const [open, setOpen] = useState(defaultOpen);
  // Follow the parent's view-driven default whenever it changes (Kanban → Weekly Grid, etc.)
  useEffect(() => { setOpen(defaultOpen); }, [defaultOpen, viewKey]);
  const now = new Date();
  const [monthState, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const month = monthProp || monthState;
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [logForTargetId, setLogForTargetId] = useState(null);
  const [editForTargetId, setEditForTargetId] = useState(null);

  const reqId = useRef(0);
  const load = useCallback(async () => {
    setLoading(true);
    const my = ++reqId.current;
    try {
      const { data } = await api.get("/department-targets", { params: { month } });
      if (my !== reqId.current) return;
      setTargets(data.targets || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load targets");
    } finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  // ---- Visibility scoping ----
  // Regular members & managers → only their own team.
  // Admins / Super-admins → all teams by default; when the parent passes a
  // `teamScope` (i.e. the Whiteboard team filter is set to a specific team),
  // narrow the panel to that team too.
  const isElevated = !!(currentUser?.is_super_admin || currentUser?.is_admin);
  const effectiveTeamScope = isElevated ? teamScope : (currentUser?.team || null);

  const scopedTargets = useMemo(() => {
    if (!effectiveTeamScope) return targets;
    return targets.filter((t) => t.team === effectiveTeamScope);
  }, [targets, effectiveTeamScope]);

  const canManage = (team) => {
    if (currentUser?.is_super_admin || currentUser?.is_admin) return true;
    return currentUser?.role === "manager" && (currentUser?.team || "") === team;
  };
  const canLog = (t) => canManage(t.team) || (t.assignee_id ? t.assignee_id === currentUser?.id : (currentUser?.team || "") === t.team);
  const canCreate = !!(currentUser?.is_super_admin || currentUser?.is_admin || currentUser?.role === "manager");

  const grouped = useMemo(() => {
    const byTeam = {};
    for (const t of scopedTargets.filter((x) => x.scope !== "individual")) {
      (byTeam[t.team] = byTeam[t.team] || []).push(t);
    }
    return byTeam;
  }, [scopedTargets]);
  const byPerson = useMemo(() => {
    const m = {};
    for (const t of scopedTargets.filter((x) => x.scope === "individual")) {
      const k = `${t.assignee_name}|${t.team}`;
      (m[k] = m[k] || []).push(t);
    }
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [scopedTargets]);

  const totalTargets = scopedTargets.length;
  const removeTarget = async (t) => {
    if (!window.confirm(`Delete target "${t.name}"? It stops from this month on.`)) return;
    try {
      await api.delete(`/department-targets/${t.id}`);
      toast.success("Target removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };
  const monthLabel = new Date(`${month}-01`).toLocaleString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="mb-3 rounded-lg border border-white/10 bg-black/25" data-testid="dept-targets-panel">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
        <button
          onClick={() => setOpen(!open)}
          data-testid="dept-targets-toggle"
          className="flex items-center gap-1.5 text-white/80 hover:text-white"
        >
          <Target size={13} className="text-red-300" />
          <span className="overline">
            Targets — {monthLabel}
            {effectiveTeamScope && <span className="ml-1 text-white/50">· {effectiveTeamScope}</span>}
          </span>
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        <span className="text-[0.7rem] font-mono text-white/40">{totalTargets} target{totalTargets === 1 ? "" : "s"}</span>
        <div className="ml-auto flex items-center gap-2">
          {!monthProp && <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="dept-targets-month"
            className="bg-black/40 border-white/10 h-7 w-36 text-xs"
          />}
          {canCreate && (
            <Button
              onClick={() => setShowAdd(true)}
              data-testid="dept-targets-add-btn"
              size="sm"
              className="bg-[#E11D2E] hover:bg-[#c1172a] h-7 rounded-full text-xs px-3"
            >
              <Plus size={11} className="mr-1" /> Add target
            </Button>
          )}
        </div>
      </div>

      {open && (
        <div className="p-3 space-y-3">
          {loading && <div className="text-xs text-white/40">Loading…</div>}
          {!loading && totalTargets === 0 && (
            <div className="text-xs text-white/40 italic py-2 text-center">
              No targets set for {monthLabel}
              {effectiveTeamScope ? ` in ${effectiveTeamScope}` : ""}.
              {canCreate && " Click Add target to create the first one."}
            </div>
          )}
          {TEAMS.filter((tm) => grouped[tm]?.length).map((tm) => (
            <div key={tm}>
              <div className="text-[0.7rem] uppercase tracking-widest text-white/50 font-mono mb-1.5 flex items-center gap-1.5">
                <Users size={10} /> {tm}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {grouped[tm].map((t) => (
                  <TargetCard
                    key={t.id}
                    target={t}
                    month={month}
                    canLog={canLog(t)}
                    canManage={canManage(t.team)}
                    onLog={() => setLogForTargetId(t.id)}
                    onEdit={() => setEditForTargetId(t.id)}
                    onDelete={async () => {
                      if (!window.confirm(`Delete target "${t.name}"?`)) return;
                      try {
                        await api.delete(`/department-targets/${t.id}`);
                        toast.success("Target removed");
                        await load();
                      } catch (e) {
                        toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
          {byPerson.length > 0 && (
            <div className="pt-2" data-testid="individual-targets">
              <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><User size={14} className="text-[#E11D2E]" /> Individual targets</div>
              <div className="space-y-3">
                {byPerson.map(([k, list]) => (
                  <div key={k}>
                    <div className="text-xs text-white/60 mb-1.5">{list[0].assignee_name} · {list[0].team}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                      {list.map((t) => (
                        <TargetCard key={t.id} target={t} month={month} canLog={canLog(t)} canManage={canManage(t.team)}
                          onLog={() => setLogForTargetId(t.id)} onEdit={() => setEditForTargetId(t.id)}
                          onDelete={() => removeTarget(t)} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showAdd && (
        <AddTargetDialog
          month={month}
          currentUser={currentUser}
          defaultTeam={effectiveTeamScope}
          onClose={() => setShowAdd(false)}
          onCreated={async () => { setShowAdd(false); await load(); }}
        />
      )}
      {logForTargetId && (
        <LogProgressDialog
          target={targets.find((t) => t.id === logForTargetId)}
          onClose={() => setLogForTargetId(null)}
          onLogged={async () => { setLogForTargetId(null); await load(); }}
        />
      )}
      {editForTargetId && (
        <EditTargetDialog
          target={targets.find((t) => t.id === editForTargetId)}
          onClose={() => setEditForTargetId(null)}
          onSaved={async () => { setEditForTargetId(null); await load(); }}
        />
      )}
    </div>
  );
}

function TargetCard({ target, month, canLog, canManage, onLog, onEdit, onDelete }) {
  const totalDone = target.progress_log.reduce((s, e) => s + (e.count || 0), 0);
  const monthly = target.monthly_count || 0;
  const remaining = Math.max(0, monthly - totalDone);
  const pct = monthly > 0 ? Math.min(100, Math.round((totalDone / monthly) * 100)) : 0;
  const hit = monthly > 0 && totalDone >= monthly;

  // Weekly split by working days (Mon–Sat) — computed on the server.
  const weekTotals = (target.weeks || []).map((w) => ({
    label: `W${w.index}`, key: w.start, done: w.done, target: w.target,
    range: `${w.start.slice(8)}–${w.end.slice(8)}`,
  }));

  return (
    <div className="rounded-md border border-white/10 bg-black/30 p-2.5" data-testid={`dept-target-${target.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate flex items-center gap-1.5">
            {target.name}
            {hit && <span className="text-[0.65rem] uppercase tracking-widest text-emerald-300">✓ hit</span>}
          </div>
          {target.notes && <div className="text-[0.7rem] text-white/50 truncate">{target.notes}</div>}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-lg font-display leading-none">
            {totalDone}<span className="text-white/40">/{monthly || "—"}</span>
          </div>
          <div className={`text-[0.7rem] font-mono ${remaining === 0 ? "text-emerald-300/80" : "text-amber-300/80"}`}>
            {remaining === 0 ? "✓ done" : `${remaining} left`}
          </div>
        </div>
      </div>
      <div className="relative h-1.5 rounded-full bg-white/5 overflow-hidden mb-2">
        <div
          className={`absolute inset-y-0 left-0 ${hit ? "bg-emerald-400" : "bg-red-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="grid gap-1 mb-2" style={{ gridTemplateColumns: `repeat(${weekTotals.length || 1}, minmax(0, 1fr))` }}>
        {weekTotals.map((w) => (
          <div
            key={w.key}
            className={`rounded px-1.5 py-1 text-center border ${
              w.done >= w.target && w.target > 0
                ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-200"
                : w.done > 0
                ? "bg-yellow-500/10 border-yellow-500/40 text-yellow-200"
                : "bg-white/5 border-white/10 text-white/50"
            }`}
            title={`${w.label} · ${w.range} · target ${w.target}, done ${w.done}`}
            data-testid={`dept-target-${target.id}-${w.label.toLowerCase()}`}
          >
            <div className="text-[0.7rem] font-medium">{w.label} <span className="opacity-60">{w.range}</span></div>
            <div className="text-xs font-medium">
              {w.done}<span className="text-white/30 text-[0.7rem]">/{w.target || "—"}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5">
        {canLog && (
          <Button
            onClick={onLog}
            size="sm"
            data-testid={`dept-target-${target.id}-log-btn`}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs px-2.5"
          >
            <Plus size={11} className="mr-1" /> Log delivery
          </Button>
        )}
        {canManage && (
          <>
            <button
              onClick={onEdit}
              data-testid={`dept-target-${target.id}-edit-btn`}
              className="text-white/50 hover:text-white p-1"
              title="Edit target"
            ><Edit3 size={12} /></button>
            <button
              onClick={onDelete}
              data-testid={`dept-target-${target.id}-delete-btn`}
              className="text-white/50 hover:text-red-400 p-1"
              title="Delete target"
            ><Trash2 size={12} /></button>
          </>
        )}
        <div className="ml-auto text-[0.7rem] font-mono text-white/40" title={`${target.progress_log.length} log entry${target.progress_log.length === 1 ? "" : "ies"}`}>
          {target.progress_log.length} log{target.progress_log.length === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}

function AddTargetDialog({ month, currentUser, defaultTeam, onClose, onCreated }) {
  // Managers get pre-picked to their own team; admins/super-admins can pick any.
  const canPickAnyTeam = currentUser?.is_super_admin || currentUser?.is_admin;
  const [team, setTeam] = useState(
    defaultTeam || (canPickAnyTeam ? "Edit Team" : (currentUser?.team || "Edit Team"))
  );
  const [name, setName] = useState("");
  const [count, setCount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState("team");
  const [assignee, setAssignee] = useState("");
  const [members, setMembers] = useState([]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setMembers(data || [])).catch(() => {});
  }, []);
  const teamMembers = members.filter((u) => u.team === team);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !count || (scope === "individual" && !assignee)) return;
    setBusy(true);
    try {
      await api.post("/department-targets", {
        team,
        name: name.trim(),
        monthly_count: Math.max(0, Math.floor(parseFloat(String(count).replace(",", ".")))) || 0,
        month_year: month,
        notes: notes.trim(),
        assignee_id: scope === "individual" ? assignee : null,
      });
      toast.success("Target added");
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add target");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell onClose={onClose} title="Add monthly target">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="overline">Target for</label>
          <div className="mt-1 grid grid-cols-2 gap-1.5" role="group">
            {[["team", "Whole department"], ["individual", "One person"]].map(([k, l]) => (
              <button key={k} type="button" aria-pressed={scope === k} onClick={() => setScope(k)}
                data-testid={`dept-target-new-scope-${k}`}
                className={`h-9 rounded-md border text-sm transition-colors ${scope === k ? "border-[#E11D2E]/60 bg-[#E11D2E]/10 font-semibold" : "border-white/10 text-white/60 hover:bg-white/5"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="overline">Team</label>
          <select
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            disabled={!canPickAnyTeam}
            data-testid="dept-target-new-team"
            className="mt-1 w-full bg-black/40 border border-white/10 rounded-md h-10 px-2 text-sm disabled:opacity-60"
          >
            {TEAMS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {scope === "individual" && (
          <div>
            <label className="overline">Person</label>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="dept-target-new-assignee"
              className="mt-1 w-full bg-black/40 border border-white/10 rounded-md h-10 px-2 text-sm">
              <option value="">Select a team member…</option>
              {teamMembers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="overline">Deliverable name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. RIIM Videos" autoFocus data-testid="dept-target-new-name" className="bg-black/40 border-white/10 mt-1" />
        </div>
        <div>
          <label className="overline">Monthly count</label>
          <Input value={count} onChange={(e) => setCount(e.target.value)} placeholder="e.g. 30" inputMode="numeric" data-testid="dept-target-new-count" className="bg-black/40 border-white/10 mt-1" />
        </div>
        <div>
          <label className="overline">Notes (optional)</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Client name, contract ref…" data-testid="dept-target-new-notes" className="bg-black/40 border-white/10 mt-1" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} className="border-white/15">Cancel</Button>
          <Button type="submit" disabled={busy || !name.trim() || !count || (scope === "individual" && !assignee)} data-testid="dept-target-new-save" className="bg-[#E11D2E] hover:bg-[#c1172a]">
            {busy ? "Saving…" : "Add"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function EditTargetDialog({ target, onClose, onSaved }) {
  const [name, setName] = useState(target?.name || "");
  const [count, setCount] = useState(String(target?.monthly_count || ""));
  const [notes, setNotes] = useState(target?.notes || "");
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/department-targets/${target.id}`, {
        name: name.trim(),
        monthly_count: Math.max(0, Math.floor(parseFloat(String(count).replace(",", ".")))) || 0,
        notes: notes.trim(),
      });
      toast.success("Target updated");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell onClose={onClose} title={`Edit — ${target.name}`}>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="overline">Deliverable name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="dept-target-edit-name" className="bg-black/40 border-white/10 mt-1" />
        </div>
        <div>
          <label className="overline">Monthly count</label>
          <Input value={count} onChange={(e) => setCount(e.target.value)} inputMode="numeric" data-testid="dept-target-edit-count" className="bg-black/40 border-white/10 mt-1" />
        </div>
        <div>
          <label className="overline">Notes</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="dept-target-edit-notes" className="bg-black/40 border-white/10 mt-1" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} className="border-white/15">Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="dept-target-edit-save" className="bg-[#E11D2E] hover:bg-[#c1172a]">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </form>
    </ModalShell>
  );
}

function LogProgressDialog({ target, onClose, onLogged }) {
  const today = new Date();
  const [count, setCount] = useState("1");
  const [date, setDate] = useState(today.toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!target) return null;

  const totalDone = target.progress_log.reduce((s, e) => s + (e.count || 0), 0);
  const remaining = Math.max(0, (target.monthly_count || 0) - totalDone);

  const submit = async (e) => {
    e.preventDefault();
    const n = Math.floor(parseFloat(String(count).replace(",", ".")));
    if (!Number.isFinite(n) || n === 0) { toast.error("Enter a non-zero count"); return; }
    setBusy(true);
    try {
      await api.post(`/department-targets/${target.id}/progress`, {
        count: n,
        date_iso: date,
        note: note.trim(),
      });
      toast.success(`+${n} logged`);
      onLogged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Log failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell onClose={onClose} title={`Log delivery — ${target.name}`}>
      <div className="mb-3 text-xs text-white/60 font-mono">
        {totalDone} of {target.monthly_count} done · {remaining} still to go this month
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="overline">How many delivered?</label>
            <Input value={count} onChange={(e) => setCount(e.target.value)} inputMode="numeric" autoFocus data-testid="dept-target-log-count" className="bg-black/40 border-white/10 mt-1" />
          </div>
          <div>
            <label className="overline">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dept-target-log-date" className="bg-black/40 border-white/10 mt-1" />
          </div>
        </div>
        <div>
          <label className="overline">Note (optional)</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Batch info, links, …" data-testid="dept-target-log-note" className="bg-black/40 border-white/10 mt-1" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} className="border-white/15">Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="dept-target-log-save" className="bg-emerald-600 hover:bg-emerald-700">{busy ? "Saving…" : "Log"}</Button>
        </div>
      </form>
      {target.progress_log.length > 0 && (
        <div className="mt-4 border-t border-white/5 pt-3">
          <div className="overline mb-1.5">Recent deliveries</div>
          <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
            {[...target.progress_log].reverse().slice(0, 15).map((e, i) => (
              <div key={i} className="text-xs flex items-center gap-2 py-0.5">
                <span className="text-emerald-300/80 font-mono w-8 text-right">+{e.count}</span>
                <span className="text-white/50 font-mono">{e.date}</span>
                <span className="text-white/70 truncate flex-1">{e.note || <span className="text-white/30 italic">—</span>}</span>
                <span className="text-white/40 text-[0.7rem]">{e.by_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950/50 backdrop-blur-[2px] flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-surface border border-white/10 rounded-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-display tracking-tight">{title}</div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1" data-testid="dept-target-dialog-close"><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
